import { createFileRoute, Link } from "@tanstack/react-router";
import { Brain, Bell, Share2, ArrowRight, Coffee, Plane, Mic, Network } from "lucide-react";
import { FeaturePage } from "@/components/marketing/FeaturePage";
import { alternateLinkList } from "@/lib/i18n/locale";
import hero from "@/assets/hero-card.webp";
import memory from "@/assets/ai-abstract.jpg";
import nudge from "@/assets/float-profile.png";
import profile from "@/assets/phone-card.jpg";

export const Route = createFileRoute("/el/solutions/founders")({
  head: () => ({
    meta: [
      { title: "Το σύστημα διαχείρισης επαφών για ιδρυτές — banana.cards" },
      { name: "description", content: "Επενδυτές, συνεργάτες, πελάτες, νέοι εργαζόμενοι — το Banana θυμάται κάθε συζήτηση και σας ειδοποιεί τη στιγμή που αποκτά ξανά σημασία." },
      { property: "og:title", content: "Το σύστημα διαχείρισης επαφών για ιδρυτές — banana.cards" },
      { property: "og:description", content: "Επενδυτές, συνεργάτες, πελάτες — το Banana δεν σας αφήνει να χάσετε μια σχέση που αξίζει." },
      { property: "og:image", content: hero },
      { property: "og:locale", content: "el_GR" },
    ],
    links: alternateLinkList("/solutions/founders", "el"),
  }),
  component: Page,
});

function Page() {
  return (
    <FeaturePage
      eyebrow="Για ιδρυτές"
      title="Κάθε συζήτηση"
      italic="μετράει."
      description="Το δίκτυό σας είναι το ανταγωνιστικό σας πλεονέκτημα. Το Banana είναι ο δεύτερος εγκέφαλος που θυμάται κάθε καφέ, γνωριμία και προσωπικό μήνυμα — και σας λέει πότε είναι ώρα για follow-up."
      heroImage={hero}
      metrics={[
        { v: "3.200+", l: "Μέσος αριθμός επαφών υπό διαχείριση" },
        { v: "92%", l: "Νέες γνωριμίες με απάντηση εντός 48 ωρών" },
        { v: "1", l: "Προφίλ σε κάθε κανάλι" },
      ]}
      bullets={[
        { icon: Brain, title: "Μνήμη AI για κάθε άνθρωπο", desc: "Σημειώσεις, φωνητικά μηνύματα, ενημερώσεις LinkedIn, email και συναντήσεις — συνοψίζονται αυτόματα σε ένα δυναμικό προφίλ για κάθε επαφή." },
        { icon: Bell, title: "Υπενθυμίσεις πριν χαθεί η επαφή", desc: "Το Banana παρακολουθεί γύρους χρηματοδότησης, αλλαγές θέσης και μεγάλα διαστήματα σιωπής — και σας θυμίζει να επικοινωνήσετε την κατάλληλη στιγμή." },
        { icon: Share2, title: "Ένα προφίλ, σε κάθε κανάλι", desc: "Κάρτα NFC, QR, Apple Wallet, υπογραφή email, LinkedIn — ενημερώνετε μία φορά, συγχρονίζονται παντού. Πάντα η πιο πρόσφατη παρουσίασή σας." },
      ]}
      deepDives={[
        {
          eyebrow: "Μνήμη",
          title: "Μην αρχίσετε ποτέ ξανά με το",
          italic: "«θυμίστε μου, πού γνωριστήκαμε;»",
          description: "Κάθε συνάντηση απομαγνητοφωνείται, κάθε φωνητικό μήνυμα συνοψίζεται, κάθε email ανασύρεται — όλα σε μια σύντομη παράγραφο για τον άνθρωπο με τον οποίο πρόκειται να μιλήσετε.",
          points: [
            "Βοηθός σημειώσεων AI για Zoom, Meet & Teams",
            "Φωνητικά μηνύματα → αναζητήσιμες σημειώσεις",
            "Ιστορικό email & ημερολογίου ανά επαφή",
            "Σύνοψη στα εισερχόμενά σας 15 λεπτά πριν από τη συνάντηση",
          ],
          image: memory,
        },
        {
          eyebrow: "Υπενθυμίσεις",
          title: "Μείνετε στο μυαλό τους,",
          italic: "χωρίς να γίνεστε ενοχλητικοί.",
          description: "Ορίστε συχνότητα επικοινωνίας ανά επαφή — εβδομαδιαία για συνιδρυτές, τριμηνιαία για επενδυτές αγγέλους, ετήσια για παλιούς συναδέλφους. Το Banana θα σας τους θυμίσει την κατάλληλη στιγμή, με ένα έτοιμο προσχέδιο μηνύματος.",
          points: [
            "Συχνότητα follow-up ανά επαφή",
            "Ειδοποιήσεις αλλαγής θέσης & χρηματοδότησης",
            "Υπενθυμίσεις γενεθλίων & επετείων",
            "Μηνύματα επικοινωνίας από AI, με το ύφος σας",
          ],
          image: nudge,
          reverse: true,
        },
        {
          eyebrow: "Κοινοποίηση",
          title: "Ένας σύνδεσμος αρκεί",
          italic: "για να συστηθείτε.",
          description: "Το προφίλ σας στο Banana είναι η παρουσίασή σας, το ημερολόγιο, το portfolio και η επαγγελματική σας κάρτα — όλα σε ένα άγγιγμα ή QR. Προσθέστε το μία φορά στην υπογραφή email σας και δεν θα χρειαστεί να επικολλήσετε ξανά σύνδεσμο Calendly.",
          points: [
            "Κάρτες NFC από μέταλλο, ξύλο, χρυσό & PVC",
            "Κάρτες Apple Wallet & Google Wallet",
            "Εργαλείο δημιουργίας υπογραφής email",
            "Εταιρικός σύνδεσμος με το δικό σας domain",
          ],
          image: profile,
        },
      ]}
      howItWorks={[
        { title: "Εισαγάγετε το δίκτυό σας", desc: "Συνδέστε Gmail, LinkedIn CSV, iCloud και Calendly — το Banana αφαιρεί διπλοεγγραφές και εμπλουτίζει τα στοιχεία στο παρασκήνιο." },
        { title: "Ορίστε τη συχνότητα επικοινωνίας", desc: "Προσθέστε ετικέτες σε επενδυτές, πελάτες και φίλους. Ορίστε πόσο συχνά θέλετε να επικοινωνείτε με κάθε κατηγορία." },
        { title: "Αφήστε το AI να ακούει", desc: "Προσθέστε τον βοηθό σημειώσεων στις κλήσεις σας. Προωθήστε φωνητικά μηνύματα. Το Banana χτίζει τη μνήμη σας." },
        { title: "Κάντε follow-up στην ώρα του", desc: "Ανοίξτε την καθημερινή σύνοψη «με ποιους να επικοινωνήσετε» και στείλτε το έτοιμο μήνυμα με δύο αγγίγματα." },
      ]}
      capabilities={[
        "Βοηθός σημειώσεων AI (50+ γλώσσες)",
        "Φωνητικό μήνυμα → CRM σε ένα βήμα",
        "Ετικέτες επενδυτών & πίνακα μετοχικής σύνθεσης",
        "Εμπλουτισμός LinkedIn & email",
        "Κάρτες Apple Wallet & Google Wallet",
        "Εργαλείο δημιουργίας υπογραφής email",
        "Σύνδεσμοι κράτησης ραντεβού",
        "Κρυπτογράφηση σημειώσεων από άκρο σε άκρο",
      ]}
      faq={[
        { q: "Είναι απλώς ένα εξελιγμένο ευρετήριο επαφών;", a: "Όχι — το Banana διαχειρίζεται ενεργά τις σχέσεις σας. Τα ευρετήρια αποθηκεύουν δεδομένα· το Banana παρακολουθεί ενδείξεις, όπως νέα θέση, χρηματοδότηση ή 90 ημέρες σιωπής, και σας ειδοποιεί όταν αξίζει να επικοινωνήσετε, με έτοιμο προσχέδιο στο ύφος σας." },
        { q: "Πώς παραμένει ακριβής η μνήμη AI;", a: "Κάθε συνάντηση με τον βοηθό σημειώσεων Banana, κάθε φωνητικό μήνυμα που προωθείτε και κάθε νήμα email με ετικέτα συνοψίζονται στο προφίλ της επαφής. Οι παλιές σημειώσεις δεν αντικαθίστανται — ταξινομούνται χρονολογικά, ώστε να βλέπετε ολόκληρο το ιστορικό." },
        { q: "Μπορώ να κρατήσω ορισμένες επαφές ιδιωτικές από την ομάδα μου;", a: "Ναι. Οι ιδρυτές που διαχειρίζονται την εταιρεία τους στο Banana μπορούν να χαρακτηρίσουν επαφές ως προσωπικές. Δεν εμφανίζονται στο CRM της ομάδας ή στα κοινόχρηστα pipeline και είναι ορατές μόνο σε εσάς. Συνήθως εδώ διατηρούνται οι λίστες επενδυτών." },
        { q: "Τι γίνεται με τα δεδομένα μου αν αποχωρήσω;", a: "Σας ανήκουν. Εξαγάγετε κάθε επαφή, σημείωση και εγγραφή σε JSON ή CSV όποτε θέλετε. Οι σημειώσεις κρυπτογραφούνται από άκρο σε άκρο και δεν εκπαιδεύουμε ποτέ μοντέλα AI με τα δεδομένα σας." },
        { q: "Υπάρχει εφαρμογή iOS / Android;", a: "Ναι — και εκεί εργάζονται οι περισσότεροι ιδρυτές. Οι εγγενείς εφαρμογές για iPhone και Android προσφέρουν κοινοποίηση με άγγιγμα, φωνητικές σημειώσεις, ειδοποιήσεις υπενθύμισης και την καθημερινή σύνοψη «με ποιους να επικοινωνήσετε»." },
      ]}
      outro={{
        title: "Το δίκτυό σας είναι",
        italic: "το ασυναγώνιστο πλεονέκτημά σας.",
        description: "Μην το αφήνετε να χάνεται ανάμεσα σε τρία CRM, μια εφαρμογή Notes και 14.000 συνδέσεις LinkedIn. Συγκεντρώστε το εκεί που ανήκει.",
      }}
      afterFaq={<FounderUseCases />}
    />
  );
}

function FounderUseCases() {
  const items = [
    { i: Coffee, t: "Άντληση κεφαλαίων", d: "Παρακολουθήστε κάθε συζήτηση με επενδυτές, ξεχωρίστε τις ενεργές από τις ψυχρές επαφές και λάβετε υπενθύμιση μία εβδομάδα πριν από τον επόμενο γύρο.", to: "/el/platform/pipelines" },
    { i: Plane, t: "Συνέδρια & ταξίδια", d: "Αγγίξτε την κάρτα σας στην έκθεση, συγχρονίστε το CRM πριν επιβιβαστείτε και κάντε follow-up πριν σας προλάβει το jet lag.", to: "/platform/events" },
    { i: Mic, t: "Podcast & Τύπος", d: "Αποθηκεύστε παρουσιαστές, δημοσιογράφους και παραγωγούς σε μία λίστα με ετικέτες. Επικοινωνήστε ξανά όταν έχετε νέα.", to: "/el/platform/crm" },
    { i: Network, t: "Προσλήψεις & σύμβουλοι", d: "Κρατήστε μια ιδιωτική λίστα ιδανικών υποψηφίων. Λάβετε ετήσιες υπενθυμίσεις μέχρι να έρθει η κατάλληλη στιγμή.", to: "/el/platform/contacts" },
  ];
  return (
    <section className="bg-background">
      <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Εφαρμογές για ιδρυτές</p>
        <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
          Κάθε ρόλος που αναλαμβάνετε, <em className="italic">με πλήρη κάλυψη.</em>
        </h2>
        <div className="mt-10 grid gap-4 md:grid-cols-2">
          {items.map((i) => {
            const Icon = i.i;
            return (
              <Link key={i.t} to={i.to} className="group rounded-2xl bg-muted p-6 transition hover:bg-muted/70">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-soft">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 text-lg font-bold">{i.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{i.d}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold underline-offset-4 group-hover:underline">
                  Εξερευνήστε <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}